import React, { FC, useState } from 'react';
import { createUseStyles } from 'src/theming';
import { Button, CardContent, Divider, Typography } from '@material-ui/core';
import { MdChevronLeft as BackIcon } from '@react-icons/all-files/md/MdChevronLeft';
import Request, { RequestStatus, RequestUpdateOptions } from 'src/interfaces/api/Request';
import { Skeleton } from '@material-ui/lab';
import Site from 'src/interfaces/api/Site';
import UpdateRequestDialog from './components/UpdateRequestDialog';
import TicketField from './components/TicketField';
import Contact from 'src/interfaces/api/Contact';
import SiteLink from './components/SiteLink';
import CustomerMessage from './components/CustomerMessage';
import EmployeeMessage from './components/EmployeeMessage';
import Card from 'src/components/Card';

const useStyles = createUseStyles({
  header: {
    marginBottom: '1rem',
  },
  subtitleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    '& h6': {
      color: '#b2b6c3',
    },
  },
  bottomContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  button: {
    textTransform: 'none !important',
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '10px !important',
  },
  divider: {
    marginTop: '15px !important',
    marginBottom: '15px !important',
  },
});

interface Props {
  updateIssueId: (requestId: string, issueId: string) => void;
  updateStatus: (requestId: string, newStatus: string, closedReason?: string) => void;
  goBack: () => void;
  request?: Request;
  site?: Site;
  creator?: Contact;
}

const RequestView: FC<Props> = ({ updateIssueId, updateStatus, goBack, request, creator, site }) => {
  const classes = useStyles();
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);

  const handleUpdateRequestStatus = async (requestOptions: RequestUpdateOptions) => {
    if (request?.id) {
      try {
        await updateStatus(request.id, requestOptions.status, requestOptions.closedReason);
        setUpdateDialogOpen(false);
      } catch (e) {
        throw new Error(e.message);
      }
    }
  };

  return (
    <div id='appView' className='div-entity'>
      <Button className={classes.button} onClick={goBack}>
        <BackIcon size={25} /> Retour à la liste
      </Button>

      <Card>
        <CardContent>
          <div className={classes.header}>
            {request ? (
              <Typography variant='h5'>
                Demande {request.id} - {request.subject}
              </Typography>
            ) : (
              <Skeleton height={50} width={250} />
            )}
            {request ? (
              <div className={classes.subtitleContainer}>
                <Typography variant='subtitle1'>{request.status}</Typography>
                <Typography variant='subtitle1'>
                  <SiteLink site={site} />
                </Typography>
              </div>
            ) : (
              <Skeleton width={50} />
            )}
          </div>

          {request && (
            <div className={classes.actionsContainer}>
              <TicketField request={request} updateIssueId={updateIssueId} />
            </div>
          )}

          <Divider className={classes.divider} component='div' />

          <CustomerMessage
            loading={request === undefined}
            creator={creator}
            createdAt={request?.createdAt}
            message={request?.message}
            urgency={request?.urgency}
            attachmentsIds={request?.attachmentsIds}
          />

          {request?.status === RequestStatus.CLOSED &&
            (request.closedReason?.initiatorType === 'employee' ? (
              <EmployeeMessage
                createdAt={request.closedReason?.date}
                message={`Raison de la cloture:\n${request.closedReason?.details}`}
              />
            ) : (
              <CustomerMessage
                creator={creator}
                createdAt={request.closedReason?.date}
                message={`Raison de la cloture:\n${request.closedReason?.details}`}
              />
            ))}

          <div className={classes.bottomContainer}>
            <Button variant='contained' color='primary' onClick={() => setUpdateDialogOpen(true)}>
              Changer le statut
            </Button>
          </div>
        </CardContent>
      </Card>

      {request && (
        <UpdateRequestDialog
          request={request}
          open={updateDialogOpen}
          onClose={() => setUpdateDialogOpen(false)}
          onValidate={handleUpdateRequestStatus}
        />
      )}
    </div>
  );
};

export default RequestView;
